import React, { useEffect, useMemo, useState } from 'react';
import './styles.scss';

interface CatalogEntry {
  id: number;
  n: string;
  t: string; // "WEAP" | "ARMO" | "ALCH" | "INGR" | "MISC" | "BOOK" | "SLGM" | "KEYM" | "AMMO" | "SCRL"
  mod: string; // basename of the providing plugin - see itemCatalog.ts
  slots?: number[];
  weight?: number; // ARMO only: 0 = light, 1 = heavy, 2 = none/clothing
  anim?: number;
  spellTome?: boolean; // BOOK only: teaches a spell (vs. a skill book/plain book)
  dupCount?: number; // set of enchant/loot variants collapsed into this entry
}

interface ItemSet {
  name: string;
  items: Array<{ itemId: number; n: string }>;
}

interface ShowEventDetail {
  catalog?: CatalogEntry[];
  catalogReady?: boolean;
  loadOrder?: string[];
  // plugin basename -> category label, and the order the categories are listed in (server-side, from content)
  modCategories?: Record<string, string>;
  modCategoryOrder?: string[];
  sets?: ItemSet[];
  // Set when the browser was opened from the admin panel for another player
  target?: { actorId: string; name: string };
}

interface ShowSetsEventDetail {
  sets?: ItemSet[];
}

type GroupMode = 'type' | 'mod';

// Fallback used only when the official CK slot (see SLOT_LABELS below) is
// either absent or one of the mod-defined slots (44+, e.g. capes) that have
// no stable engine-level meaning - the slot mask is preferred whenever it's
// available since it's actual game data, not a guess. False positives here
// are expected and accepted (a stray "Robe" substring, an oddly-named item)
// - this is a staff convenience tool, not a data audit.
const NAME_KEYWORD_CATEGORIES: Array<[string, string[]]> = [
  ['Boucliers', ['shield', 'bouclier', 'targe', 'buckler']],
  ['Capes', ['cloak', 'cape', 'pèlerine', 'pelerine', 'mantle']],
  ['Diadèmes', ['circlet', 'diadème', 'diademe', 'crown', 'couronne', 'tiara']],
  ['Casques', ['helmet', 'hood', 'casque', 'capuche', 'coif', 'cowl', 'mask', 'masque']],
  ['Gants', ['gauntlet', 'glove', 'gant', 'bracer', 'brassard', 'vambrace']],
  ['Bottes', ['boot', 'botte', 'shoe', 'chaussure', 'sandal']],
  ['Anneaux', ['ring', 'anneau', 'bague']],
  ['Amulettes', ['amulet', 'amulette', 'necklace', 'collier', 'pendant']],
  ['Ceintures', ['belt', 'ceinture', 'sash']],
  ['Robes / tenues', ['robe', 'dress', 'tunic', 'tunique', 'vestment', 'apparel']],
  ['Armures (torse)', ['cuirass', 'cuirasse', 'armor', 'armure', 'breastplate', 'chestplate', 'vest']],
];

function nameKeywordCategory(name: string): string | null {
  const lower = name.toLowerCase();
  for (const [category, keywords] of NAME_KEYWORD_CATEGORIES) {
    if (keywords.some((kw) => lower.indexOf(kw) !== -1)) return category;
  }
  return null;
}

// CK biped object slots 30-43 - the only ones with a stable vanilla meaning.
// Used as the primary sub-category within an armor weight class (see
// armorSlotSub below). Anything else (44-61) is mod-convention-defined with
// no universal name, so it falls back to the keyword table or a numbered
// label instead.
const SLOT_LABELS: Record<number, string> = {
  30: 'Tête', 31: 'Cheveux', 32: 'Corps', 33: 'Mains', 34: 'Avant-bras',
  35: 'Amulette', 36: 'Anneau', 37: 'Pieds', 38: 'Mollets', 39: 'Bouclier',
  40: 'Queue', 41: 'Longs cheveux', 42: 'Diadème', 43: 'Oreilles',
};

// espm::WEAP::AnimType (libespm/include/libespm/WEAP.h) - byte 0 of DNAM.
const ANIM_LABELS: Record<number, string> = {
  0: 'Autre arme', 1: 'Épées', 2: 'Dagues', 3: 'Haches', 4: 'Masses',
  5: 'Épées à 2 mains', 6: 'Haches à 2 mains', 7: 'Arcs', 8: 'Bâtons', 9: 'Arbalètes',
};

function animLabel(anim: number): string {
  return ANIM_LABELS[anim] ?? 'Autre arme';
}

const ALL_PART_KEYWORDS = NAME_KEYWORD_CATEGORIES.flatMap(([, keywords]) => keywords);
const CONNECTOR_WORDS = new Set(['de', 'du', 'des', 'the', 'a', 'an', 'le', 'la', 'les', 'of', 'd', 'l']);

// Heuristic "which armor set does this piece belong to" key - strips the
// part word (helmet/boots/gauntlets/...) and connector words, leaving just
// the material/set name, e.g. "Cuirasse de fer" and "Bottes de fer" both
// reduce to "fer" and "Iron Gauntlets"/"Iron Boots" both reduce to "iron".
// Same "false positives accepted" tradeoff as nameKeywordCategory above -
// staff convenience, not a data audit. Returns null when nothing distinctive
// is left (e.g. a bare "Casque" with no material name to group on).
function armorSetKeyOf(name: string): string | null {
  const words = name.split(/[\s'’-]+/).filter(Boolean);
  const kept = words.filter((w) => {
    const lower = w.toLowerCase();
    if (CONNECTOR_WORDS.has(lower)) return false;
    if (ALL_PART_KEYWORDS.some((kw) => lower.indexOf(kw) !== -1)) return false;
    return true;
  });
  const key = kept.join(' ').toLowerCase();
  return key.length >= 3 ? key : null;
}

// Non-WEAP/ARMO lootable types the catalog can contain (see LOOTABLE_TYPES
// in itemCatalog.ts) - each gets its own flat top-level category.
const TYPE_LABELS: Record<string, string> = {
  ALCH: 'Potions', INGR: 'Ingrédients', MISC: 'Divers', BOOK: 'Livres',
  SLGM: "Gemmes d'âme", KEYM: 'Clés', AMMO: 'Munitions', SCRL: 'Parchemins',
};

// espm::WEAP::AnimType groups - how the weapon is actually wielded, not just
// its animation. Staves (8) are held two-handed in vanilla, grouped there.
const WEAPON_SUPER_GROUPS: Record<number, { key: string; label: string }> = {
  1: { key: 'onehand', label: 'Armes à une main' },
  2: { key: 'onehand', label: 'Armes à une main' },
  3: { key: 'onehand', label: 'Armes à une main' },
  4: { key: 'onehand', label: 'Armes à une main' },
  5: { key: 'twohand', label: 'Armes à deux mains' },
  6: { key: 'twohand', label: 'Armes à deux mains' },
  8: { key: 'twohand', label: 'Armes à deux mains' },
  7: { key: 'ranged', label: 'Armes à distance' },
  9: { key: 'ranged', label: 'Armes à distance' },
};

function weaponSuperGroup(anim: number): { key: string; label: string } {
  return WEAPON_SUPER_GROUPS[anim] ?? { key: 'other', label: 'Autres armes' };
}

const ARMOR_WEIGHT_GROUPS: Record<number, { key: string; label: string }> = {
  0: { key: 'light', label: 'Armure légère' },
  1: { key: 'heavy', label: 'Armure lourde' },
  2: { key: 'none', label: 'Vêtements & bijoux' },
};

function armorWeightGroup(weight: number | undefined): { key: string; label: string } {
  return (weight !== undefined && ARMOR_WEIGHT_GROUPS[weight]) || { key: 'none', label: 'Vêtements & bijoux' };
}

// Sub-category within a weight class: the official CK slot (30-43) when
// available - reliable, not a guess - falling back to the name-keyword table
// only for mod-defined slots (44+, no stable meaning) or items with no BOD2
// slot data at all.
function armorSlotSub(entry: CatalogEntry): { key: string; label: string } {
  const slot = entry.slots?.[0];
  if (slot !== undefined) {
    if (SLOT_LABELS[slot]) return { key: String(slot), label: SLOT_LABELS[slot] };
    const kw = nameKeywordCategory(entry.n);
    if (kw) return { key: `kw:${kw}`, label: kw };
    return { key: `slot:${slot}`, label: `Emplacement ${slot}` };
  }
  const kw = nameKeywordCategory(entry.n);
  if (kw) return { key: `kw:${kw}`, label: kw };
  return { key: 'unknown', label: 'Autre' };
}

interface CategoryPath {
  topKey: string;
  topLabel: string;
  leafKey: string | null;
  leafLabel: string | null;
}

function classifyByType(entry: CatalogEntry): CategoryPath {
  if (entry.t === 'ARMO') {
    const group = armorWeightGroup(entry.weight);
    const sub = armorSlotSub(entry);
    const topKey = `armor.${group.key}`;
    return { topKey, topLabel: group.label, leafKey: `${topKey}.${sub.key}`, leafLabel: sub.label };
  }
  if (entry.t === 'WEAP') {
    const anim = entry.anim ?? 0;
    const group = weaponSuperGroup(anim);
    const topKey = `weapon.${group.key}`;
    return { topKey, topLabel: group.label, leafKey: `${topKey}.${anim}`, leafLabel: animLabel(anim) };
  }
  if (entry.t === 'BOOK' && entry.spellTome) {
    return { topKey: 'type.BOOK_SPELL', topLabel: 'Livres de sorts', leafKey: null, leafLabel: null };
  }
  const label = TYPE_LABELS[entry.t] ?? entry.t;
  return { topKey: `type.${entry.t}`, topLabel: label, leafKey: null, leafLabel: null };
}

// Mods are grouped by what they contain (races, armor, weapons, villages...),
// decided server-side from the plugin's records; the plugins of a category are
// the sub-level, in load order. A server that does not send categories leaves
// everything in "Autres".
const NO_CATEGORY = 'Autres';

function classifyByMod(entry: CatalogEntry, modCategories: Record<string, string>): CategoryPath {
  const filename = entry.mod || 'Inconnu';
  const category = modCategories[filename] ?? NO_CATEGORY;
  return { topKey: `modcat.${category}`, topLabel: category, leafKey: `mod.${filename}`, leafLabel: filename };
}

function classify(entry: CatalogEntry, groupMode: GroupMode, modCategories: Record<string, string>): CategoryPath {
  return groupMode === 'type' ? classifyByType(entry) : classifyByMod(entry, modCategories);
}

interface CategoryNode {
  key: string;
  label: string;
  count: number;
  children: CategoryNode[];
}

// Curated so armor/weapons (most relevant to an admin testing combat) come
// first, then consumables/misc alphabetically by their French label. Any
// top key not listed here (shouldn't happen - LOOTABLE_TYPES covers every
// type the catalog can contain) sorts alphabetically after.
const TOP_ORDER = [
  'armor.heavy', 'armor.light', 'armor.none', 'sets',
  'weapon.onehand', 'weapon.twohand', 'weapon.ranged', 'weapon.other',
  'type.KEYM', 'type.MISC', 'type.SLGM', 'type.INGR', 'type.BOOK', 'type.BOOK_SPELL', 'type.AMMO', 'type.SCRL', 'type.ALCH',
];

function buildCategoryTree(
  catalog: CatalogEntry[],
  groupMode: GroupMode,
  loadOrder: string[],
  armorSetCounts: Map<string, number>,
  modCategories: Record<string, string>,
  modCategoryOrder: string[]
): CategoryNode[] {
  const tops = new Map<string, { label: string; count: number; children: Map<string, { label: string; count: number }> }>();

  for (const entry of catalog) {
    const path = classify(entry, groupMode, modCategories);
    let top = tops.get(path.topKey);
    if (!top) {
      top = { label: path.topLabel, count: 0, children: new Map() };
      tops.set(path.topKey, top);
    }
    top.count++;
    if (path.leafKey) {
      const child = top.children.get(path.leafKey);
      if (child) child.count++;
      else top.children.set(path.leafKey, { label: path.leafLabel ?? path.leafKey, count: 1 });
    }
  }

  // Cross-cutting view: armor pieces that look like they belong to the same
  // material/set (see armorSetKeyOf) regardless of weight class, so staff can
  // find "give me the whole iron set" without knowing whether it's light or
  // heavy. Only sets with 2+ recognized pieces count - a single matching item
  // isn't a "set" worth its own row.
  if (groupMode === 'type') {
    const children = new Map<string, { label: string; count: number }>();
    let total = 0;
    for (const [key, count] of armorSetCounts) {
      if (count < 2) continue;
      children.set(`sets.${key}`, { label: key.charAt(0).toUpperCase() + key.slice(1), count });
      total += count;
    }
    if (children.size > 0) {
      tops.set('sets', { label: "Ensembles d'armure", count: total, children });
    }
  }

  const topKeys = [...tops.keys()];
  // loadOrder entries can be bare filenames or absolute paths (see
  // itemCatalog.ts) while entry.mod (and so a leaf key) is always a bare
  // basename - compare basenames so ranking still works either way.
  const modRank = (key: string) => {
    const modName = key.slice('mod.'.length);
    const i = loadOrder.findIndex((f) => f && (f === modName || f.replace(/^.*[\\/]/, '') === modName));
    return i === -1 ? loadOrder.length : i;
  };
  if (groupMode === 'mod') {
    const categoryRank = (key: string) => {
      const i = modCategoryOrder.indexOf(key.slice('modcat.'.length));
      return i === -1 ? modCategoryOrder.length : i;
    };
    topKeys.sort((a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b));
  } else {
    topKeys.sort((a, b) => {
      const ia = TOP_ORDER.indexOf(a);
      const ib = TOP_ORDER.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return (tops.get(a)?.label ?? a).localeCompare(tops.get(b)?.label ?? b);
    });
  }

  return topKeys.map((topKey) => {
    const top = tops.get(topKey)!;
    const childKeys = [...top.children.keys()].sort((a, b) => {
      // Mods of a category: load order
      if (groupMode === 'mod') return modRank(a) - modRank(b);
      // Armor slots: official CK order (30-43) before keyword/unknown fallbacks.
      // Weapon anims: fixed weapon-type order within the super-group.
      const na = Number(a.split('.').pop());
      const nb = Number(b.split('.').pop());
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      if (!Number.isNaN(na)) return -1;
      if (!Number.isNaN(nb)) return 1;
      return (top.children.get(a)?.label ?? a).localeCompare(top.children.get(b)?.label ?? b);
    });
    return {
      key: topKey,
      label: top.label,
      count: top.count,
      children: childKeys.map((leafKey) => ({
        key: leafKey,
        label: top.children.get(leafKey)!.label,
        count: top.children.get(leafKey)!.count,
        children: [],
      })),
    };
  });
}

const MAX_RENDERED = 400;
const HEX8 = (n: number) => '0x' + n.toString(16).padStart(8, '0');

const ItemGiver: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [catalogReady, setCatalogReady] = useState(false);
  const [target, setTarget] = useState<{ actorId: string; name: string } | null>(null);
  // The engine's 3D render of an item: 'loading' until the picture comes back, null when it failed
  const [preview, setPreview] = useState<{ id: number; name: string; image: string | null | 'loading' } | null>(null);
  const [loadOrder, setLoadOrder] = useState<string[]>([]);
  const [modCategories, setModCategories] = useState<Record<string, string>>({});
  const [modCategoryOrder, setModCategoryOrder] = useState<string[]>([]);
  const [sets, setSets] = useState<ItemSet[]>([]);
  const [search, setSearch] = useState('');
  const [groupMode, setGroupMode] = useState<GroupMode>('type');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedTop, setExpandedTop] = useState<Set<string>>(new Set());
  const [giveAllArmed, setGiveAllArmed] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [selection, setSelection] = useState<Map<number, CatalogEntry>>(new Map());
  const [setNameInput, setSetNameInput] = useState('');
  // Per-item quantity, defaulting to 1 - itemGiverSystem's `give` action
  // already accepts a `count`, only the front used to always send 1.
  const [quantities, setQuantities] = useState<Map<number, number>>(new Map());
  const quantityOf = (id: number): number => quantities.get(id) ?? 1;
  const setQuantity = (id: number, value: number): void => {
    const clamped = Math.max(1, Math.min(999, Math.floor(value) || 1));
    setQuantities((prev) => new Map(prev).set(id, clamped));
  };

  useEffect(() => {
    const showHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      setCatalog(detail.catalog ?? []);
      setCatalogReady(!!detail.catalogReady);
      setLoadOrder(detail.loadOrder ?? []);
      setModCategories(detail.modCategories ?? {});
      setModCategoryOrder(detail.modCategoryOrder ?? []);
      setSets(detail.sets ?? []);
      setTarget(detail.target ?? null);
      setSelectedCategory(null);
      setExpandedTop(new Set());
      setGiveAllArmed(false);
      setSearch('');
      setFeedback(null);
      setSelection(new Map());
      setSetNameInput('');
      setPreview(null);
      setVisible(true);
    };
    const hideHandler = () => { setVisible(false); setPreview(null); };
    const previewHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { itemId: number; image: string | null };
      setPreview((prev) => (prev && prev.id === detail.itemId ? { ...prev, image: detail.image ?? null } : prev));
    };
    const setsHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowSetsEventDetail;
      setSets(detail.sets ?? []);
    };

    window.addEventListener('skymp5-client:showItemGiver', showHandler);
    window.addEventListener('skymp5-client:hideItemGiver', hideHandler);
    window.addEventListener('skymp5-client:showItemGiverSets', setsHandler);
    window.addEventListener('skymp5-client:itemPreview', previewHandler);
    return () => {
      window.removeEventListener('skymp5-client:itemPreview', previewHandler);
      window.removeEventListener('skymp5-client:showItemGiver', showHandler);
      window.removeEventListener('skymp5-client:hideItemGiver', hideHandler);
      window.removeEventListener('skymp5-client:showItemGiverSets', setsHandler);
    };
  }, []);

  const send = (payload: Record<string, unknown>): void => {
    try {
      (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('itemGiver sendMessage', payload);
    }
  };

  // Powers both the dedicated "Ensembles d'armure" category and the give-all
  // matching below - computed once per catalog rather than re-grouping on
  // every filter/render.
  const armorSetCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entry of catalog) {
      if (entry.t !== 'ARMO') continue;
      const key = armorSetKeyOf(entry.n);
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [catalog]);

  const categoryTree = useMemo(
    () => buildCategoryTree(catalog, groupMode, loadOrder, armorSetCounts, modCategories, modCategoryOrder),
    [catalog, groupMode, loadOrder, armorSetCounts, modCategories, modCategoryOrder]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out: CatalogEntry[] = [];
    for (const entry of catalog) {
      if (selectedCategory !== null) {
        if (selectedCategory === 'sets' || selectedCategory.startsWith('sets.')) {
          if (entry.t !== 'ARMO') continue;
          const key = armorSetKeyOf(entry.n);
          if (!key || (armorSetCounts.get(key) ?? 0) < 2) continue;
          if (selectedCategory !== 'sets' && selectedCategory !== `sets.${key}`) continue;
        } else {
          const path = classify(entry, groupMode, modCategories);
          if (path.topKey !== selectedCategory && path.leafKey !== selectedCategory) continue;
        }
      }
      if (q && entry.n.toLowerCase().indexOf(q) === -1) continue;
      out.push(entry);
    }
    return out;
  }, [catalog, search, selectedCategory, groupMode, armorSetCounts, modCategories]);

  const showingAll = selectedCategory === null && !search.trim();
  const shown = showingAll ? [] : filtered.slice(0, MAX_RENDERED);
  const truncated = filtered.length > shown.length;

  // When the current view is all-armor (a category picked in "Par type"
  // mode always is, since categories are derived per-type), cluster pieces
  // that look like they belong to the same set instead of listing them in
  // plain alphabetical order.
  const isArmorOnly = shown.length > 0 && shown.every((e) => e.t === 'ARMO');
  const displayItems = (() => {
    if (!isArmorOnly) return shown.map((entry) => ({ entry, setHeader: null as string | null }));
    const withKeys = shown.map((entry) => ({ entry, key: armorSetKeyOf(entry.n) }));
    const counts = new Map<string, number>();
    for (const { key } of withKeys) if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    const sortKey = (e: { entry: CatalogEntry; key: string | null }) =>
      e.key && (counts.get(e.key) ?? 0) > 1 ? e.key : '￿' + e.entry.n.toLowerCase();
    const sorted = [...withKeys].sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      if (ka !== kb) return ka < kb ? -1 : 1;
      return a.entry.n.localeCompare(b.entry.n);
    });
    let lastGroupKey: string | null = null;
    return sorted.map(({ entry, key }) => {
      const grouped = !!key && (counts.get(key) ?? 0) > 1;
      const header = grouped && key !== lastGroupKey ? key : null;
      lastGroupKey = grouped ? key : null;
      return { entry, setHeader: header };
    });
  })();

  const giveItem = (entry: CatalogEntry): void => {
    const count = quantityOf(entry.id);
    send({ action: 'give', itemId: entry.id, count, label: entry.n });
    setFeedback(count > 1 ? `${entry.n} donné (×${count}).` : `${entry.n} donné.`);
  };

  const previewItem = (entry: CatalogEntry, e: React.SyntheticEvent): void => {
    e.stopPropagation();
    setPreview({ id: entry.id, name: entry.n, image: 'loading' });
    send({ action: 'preview', itemId: entry.id });
  };

  const toggleSelect = (entry: CatalogEntry, e: React.SyntheticEvent): void => {
    e.stopPropagation();
    setSelection((prev) => {
      const next = new Map(prev);
      if (next.has(entry.id)) next.delete(entry.id); else next.set(entry.id, entry);
      return next;
    });
  };

  const clearSelection = (): void => {
    setSelection(new Map());
    setSetNameInput('');
  };

  const giveSelection = (): void => {
    for (const entry of selection.values()) {
      send({ action: 'give', itemId: entry.id, count: 1, label: entry.n });
    }
    setFeedback(`${selection.size} objet(s) donné(s).`);
  };

  // Above this size, require a second click - a mis-click on "Donner toute
  // la catégorie" while browsing something like "Armes à une main" (which
  // can hold hundreds of entries across a full mod list) would otherwise
  // dump all of it into the inventory with no way back.
  const GIVE_ALL_CONFIRM_THRESHOLD = 100;

  const giveAllInCategory = (): void => {
    if (filtered.length > GIVE_ALL_CONFIRM_THRESHOLD && !giveAllArmed) {
      setGiveAllArmed(true);
      setFeedback(`Clique à nouveau pour confirmer : donner ${filtered.length} objets ?`);
      return;
    }
    for (const entry of filtered) {
      send({ action: 'give', itemId: entry.id, count: 1, label: entry.n });
    }
    setFeedback(`${filtered.length} objet(s) donné(s) (catégorie complète).`);
    setGiveAllArmed(false);
  };

  const selectCategory = (key: string | null): void => {
    setSelectedCategory(key);
    setGiveAllArmed(false);
  };

  const saveSet = (): void => {
    const name = setNameInput.trim();
    if (!name || selection.size === 0) return;
    send({
      action: 'saveSet',
      name,
      items: [...selection.values()].map((entry) => ({ itemId: entry.id, n: entry.n })),
    });
    clearSelection();
  };

  const giveSet = (set: ItemSet): void => {
    send({ action: 'giveSet', name: set.name });
    setFeedback(`Set "${set.name}" donné.`);
  };

  const deleteSet = (set: ItemSet, e: React.SyntheticEvent): void => {
    e.stopPropagation();
    send({ action: 'deleteSet', name: set.name });
  };

  const close = () => send({ action: 'close' });

  if (!visible) return null;

  return (
    <div className="item-giver">
      <div className="item-giver__window">
        <div className="item-giver__header">
          <div className="item-giver__title-group">
            <span className="item-giver__title">{target ? `Donneur d'objets pour ${target.name}` : "Donneur d'objets"}</span>
          </div>
          <div className="item-giver__header-right">
            {!catalogReady && (
              <span className="item-giver__building">Catalogue en cours de scan (relance F9 dans un instant)...</span>
            )}
            <button type="button" className="item-giver__button item-giver__button--quiet" onClick={close}>
              Fermer
            </button>
          </div>
        </div>

        <div className="item-giver__toolbar">
          <input
            className="item-giver__search"
            placeholder="Rechercher un objet..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setGiveAllArmed(false); }}
            autoFocus
          />
          <div className="item-giver__group-toggle">
            <button
              type="button"
              className={'item-giver__toggle-btn' + (groupMode === 'type' ? ' item-giver__toggle-btn--active' : '')}
              onClick={() => { setGroupMode('type'); selectCategory(null); setExpandedTop(new Set()); }}
            >
              Par type
            </button>
            <button
              type="button"
              className={'item-giver__toggle-btn' + (groupMode === 'mod' ? ' item-giver__toggle-btn--active' : '')}
              onClick={() => { setGroupMode('mod'); selectCategory(null); setExpandedTop(new Set()); }}
            >
              Par mod
            </button>
          </div>
        </div>

        <div className="item-giver__body">
          <div className="item-giver__sidebar">
            <div className="item-giver__sidebar-categories">
              <button
                type="button"
                className={'item-giver__category' + (selectedCategory === null ? ' item-giver__category--active' : '')}
                onClick={() => selectCategory(null)}
              >
                <span>Toutes catégories</span>
                <span className="item-giver__category-count">{catalog.length}</span>
              </button>
              {categoryTree.map((top) => {
                const hasChildren = top.children.length > 0;
                const expanded = expandedTop.has(top.key);
                return (
                  <React.Fragment key={top.key}>
                    <button
                      type="button"
                      className={'item-giver__category' + (selectedCategory === top.key ? ' item-giver__category--active' : '')}
                      onClick={() => {
                        selectCategory(top.key);
                        if (hasChildren) {
                          setExpandedTop((prev) => {
                            const next = new Set(prev);
                            if (next.has(top.key)) next.delete(top.key); else next.add(top.key);
                            return next;
                          });
                        }
                      }}
                      title={top.label}
                    >
                      {hasChildren && <span className="item-giver__category-caret">{expanded ? '▾' : '▸'}</span>}
                      <span className="item-giver__category-name">{top.label}</span>
                      <span className="item-giver__category-count">{top.count}</span>
                    </button>
                    {hasChildren && expanded && top.children.map((leaf) => (
                      <button
                        type="button"
                        key={leaf.key}
                        className={'item-giver__category item-giver__category--child' + (selectedCategory === leaf.key ? ' item-giver__category--active' : '')}
                        onClick={() => selectCategory(leaf.key)}
                        title={leaf.label}
                      >
                        <span className="item-giver__category-name">{leaf.label}</span>
                        <span className="item-giver__category-count">{leaf.count}</span>
                      </button>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="item-giver__sidebar-sets">
              <div className="item-giver__sidebar-heading">Sets sauvegardés</div>
              {sets.length === 0 ? (
                <div className="item-giver__hint-small">Sélectionne des objets (case à cocher) pour créer un set.</div>
              ) : (
                sets.map((set) => (
                  <div className="item-giver__set-row" key={set.name}>
                    <span className="item-giver__set-name" title={set.name}>
                      {set.name} <span className="item-giver__set-count">({set.items.length})</span>
                    </span>
                    <div className="item-giver__set-actions">
                      <button type="button" title="Donner tout le set" onClick={() => giveSet(set)}>Donner</button>
                      <button type="button" title="Supprimer le set" onClick={(e) => deleteSet(set, e)}>×</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="item-giver__main">
            {showingAll ? (
              <div className="item-giver__hint">
                Choisis une catégorie à gauche ou lance une recherche pour afficher des objets
                ({catalog.length} au total).
              </div>
            ) : shown.length === 0 ? (
              <div className="item-giver__hint">Aucun objet trouvé.</div>
            ) : (
              <>
                <div className="item-giver__category-actions">
                  <button
                    type="button"
                    className="item-giver__button item-giver__button--secondary"
                    onClick={giveAllInCategory}
                  >
                    {giveAllArmed
                      ? `Confirmer : donner ${filtered.length} objets`
                      : `Donner toute la catégorie (${filtered.length})`}
                  </button>
                </div>
                {truncated && (
                  <div className="item-giver__truncated">
                    {filtered.length - shown.length} résultat(s) supplémentaire(s) masqué(s) - affine ta recherche.
                  </div>
                )}
                <div className="item-giver__grid">
                  {displayItems.map(({ entry, setHeader }) => (
                    <React.Fragment key={entry.id}>
                      {setHeader && (
                        <div className="item-giver__set-header">
                          Ensemble : {setHeader.charAt(0).toUpperCase() + setHeader.slice(1)}
                        </div>
                      )}
                      <button
                        type="button"
                        className="item-giver__item"
                        onClick={(e) => {
                          // The 3D preview button and the checkbox sit inside this button: a click on them
                          // must never give the item
                          if ((e.target as HTMLElement).closest('.item-giver__item-3d, .item-giver__item-qty, .item-giver__item-check')) return;
                          giveItem(entry);
                        }}
                        title={`Donner : ${entry.n}`}
                      >
                        <input
                          type="checkbox"
                          className="item-giver__item-check"
                          checked={selection.has(entry.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => toggleSelect(entry, e)}
                          title="Ajouter à la sélection"
                        />
                        <span className="item-giver__item-name">
                          {entry.n}
                          {entry.dupCount ? (
                            <span
                              className="item-giver__item-dup"
                              title={`${entry.dupCount} variantes (enchantements différents) fusionnées`}
                            >
                              {' '}×{entry.dupCount}
                            </span>
                          ) : null}
                        </span>
                        <span
                          role="button"
                          className="item-giver__item-3d"
                          onMouseDown={(e) => e.stopPropagation()}
                          title="Aperçu 3D (rendu du moteur)"
                          onClick={(e) => previewItem(entry, e)}
                        >
                          3D
                        </span>
                        <input
                          type="number"
                          className="item-giver__item-qty"
                          min={1}
                          max={999}
                          value={quantityOf(entry.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setQuantity(entry.id, Number(e.target.value))}
                          title="Quantité à donner"
                        />
                        <span className="item-giver__item-id">{HEX8(entry.id)}</span>
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {selection.size > 0 && (
          <div className="item-giver__basket">
            <span className="item-giver__basket-count">{selection.size} objet(s) sélectionné(s)</span>
            <input
              className="item-giver__basket-name"
              placeholder="Nom du set (ex: Kit Assassin Thalmor)"
              value={setNameInput}
              onChange={(e) => setSetNameInput(e.target.value)}
            />
            <button type="button" className="item-giver__button" disabled={!setNameInput.trim()} onClick={saveSet}>
              Enregistrer le set
            </button>
            <button type="button" className="item-giver__button item-giver__button--primary" onClick={giveSelection}>
              Donner la sélection
            </button>
            <button type="button" className="item-giver__button item-giver__button--quiet" onClick={clearSelection}>
              Vider
            </button>
          </div>
        )}

        {preview && (
          <div className="item-giver__preview">
            <div className="item-giver__preview-title">{preview.name}</div>
            {preview.image === 'loading' ? (
              <div className="item-giver__preview-note">Rendu en cours...</div>
            ) : preview.image ? (
              <img className="item-giver__preview-image" src={preview.image} alt={preview.name} />
            ) : (
              <div className="item-giver__preview-note">
                Aperçu indisponible (menu du jeu ouvert, ou objet sans modèle).
              </div>
            )}
            <button type="button" className="item-giver__button item-giver__button--quiet" onClick={() => setPreview(null)}>
              Fermer l'aperçu
            </button>
          </div>
        )}

        {feedback && <div className="item-giver__feedback">{feedback}</div>}
      </div>
    </div>
  );
};

export default ItemGiver;
